import i18next from 'i18next';
import { useCallback, useMemo } from 'react';
import { Button, Col, Row, Stack } from 'react-bootstrap';
import Offcanvas, { OffcanvasPlacement } from 'react-bootstrap/Offcanvas';
import { StellantisQuoteRequestDTO } from 'src/api/models/StellantisDTO';
import * as Utils from './stellantisUtils';
import { numberFormat } from 'src/utils';
import { FaDownload } from 'react-icons/fa';
import { useStellantisQuoteAttachments } from './useStellantisQuoteAttachments';

type Props = {
  placement?: OffcanvasPlacement | undefined;
  quoteRequest?: StellantisQuoteRequestDTO | null;
  onOpenChange: (bool: boolean) => void;
  open?: boolean
}

export default function StellantisQuoteDetailOverlay({ open = false, onOpenChange, quoteRequest, placement = 'end' }: Props) {
  const { downloadState, downloadExternalPdf, downloadHmpPdf } = useStellantisQuoteAttachments();

  const canoneLabelByClientType = quoteRequest?.quote?.tipoCliente === 'privato'
    ? i18next.t('canone_with_iva')
    : i18next.t('canone_without_iva');

  const getBoolValue = (value?: boolean) => {
    return Boolean(value) ? i18next.t('yes') : i18next.t('no');
  }

  const hasOptionals = !!quoteRequest?.quote?.optionals?.length;

  const attributes = useMemo(() => {
    if (!quoteRequest?.quote) return [];

    const list: any = [
      { label: [i18next.t('client_type')], value: quoteRequest.quote.tipoCliente?.replace('_', ' ')?.toUpperCase() },
    ];

    if (Utils.isAzienda(quoteRequest)) {
      list.push({ label: [i18next.t('company')], value: quoteRequest.quote.ragioneSociale });
    } else {
      list.push(
        { label: [i18next.t('first_name')], value: quoteRequest.quote.nome },
        { label: [i18next.t('last_name')], value: quoteRequest.quote.cognome },
      );
    }

    list.push(
      { label: [i18next.t('brand')], value: quoteRequest.quote.marca },
      { label: [i18next.t('model')], value: quoteRequest.quote.modello },
      { label: [i18next.t('allestimento')], value: quoteRequest.quote.allestimento },
      { label: [i18next.t('km_annui')], value: quoteRequest.quote.kmAnnui + ' ' + i18next.t('months') },
      { label: [i18next.t('duration')], value: quoteRequest.quote.durata },
      { label: [i18next.t('downpayment_noiva')], value: numberFormat(quoteRequest.quote.anticipoNoIva || 0) + '€' },
      { label: [i18next.t('liability_insurance')], value: numberFormat(quoteRequest.quote.rcaPenaleRisarcitoria || 0) + '€' },
      { label: [i18next.t('kasko')], value: numberFormat(quoteRequest.quote.kaskoFranchigia || 0) + '€' },
      { label: [i18next.t('theft_fire')], value: quoteRequest.quote.incendioFurtoScopertoPerc + '%' },
      { label: [i18next.t('gomme_YN')], value: getBoolValue(quoteRequest.quote.gommeYN) },
    );

    if (quoteRequest.quote.gommeYN) {
      list.push(
        { label: [i18next.t('tipo_gomma')], value: quoteRequest.quote.tipoGomma },
        { label: [i18next.t('gomme_illimitate_YN')], value: getBoolValue(quoteRequest.quote.gommeIllimitateYN) },
        { label: [i18next.t('n_treno_gomme_estive')], value: quoteRequest.quote.numTrenoGommeEstive },
        { label: [i18next.t('n_treno_gomme_invernali')], value: quoteRequest.quote.numTrenoGommeInvernali },
      );
    }

    list.push({ label: [i18next.t('auto_sostitutiva_YN')], value: getBoolValue(quoteRequest.quote.autoSostitutivaYN) });

    if (quoteRequest.quote.autoSostitutivaYN) {
      list.push({ label: [i18next.t('auto_sostitutiva_gruppo')], value: quoteRequest.quote.autoSostitutivaGruppo?.toUpperCase() || '' });
    }

    list.push(
      { label: [i18next.t('rental_company')], value: quoteRequest.quote.noleggiatore },
      { label: [canoneLabelByClientType], value: numberFormat(quoteRequest.quote.canone || 0) + '€' },
      { label: [i18next.t('notes')], value: <span style={{ whiteSpace: 'pre-wrap' }}>{quoteRequest.quote.requestNote}</span> },
    )

    if (!hasOptionals) {
      list.push(
        { label: [i18next.t('optionals')], value: getBoolValue(false) },
      )
    }

    return list;

  }, [quoteRequest]);

  const renderOptionals = useCallback(() => {
    return (
      <>
        <hr />
        <Row>
          <h4>{i18next.t('optionals')}</h4>
          <Row className="mt-2 mb-1">
            <Col md={4} className="fw-semibold">{i18next.t('description')}</Col>
            <Col md={4} className="fw-semibold">{i18next.t('price')}</Col>
          </Row>
          {(quoteRequest?.quote?.optionals || []).map((opt, idx) => {
            return (
              <Row key={idx} className="mb-2">
                <Col md={4}>{opt.descrizione}</Col>
                <Col md={4}>{numberFormat(opt.valore || 0) + '€'}</Col>
              </Row>)
          })}
        </Row>
      </>
    )
  }, [quoteRequest])

  const renderPdfs = useCallback(() => {
    if (!quoteRequest) return null;

    return (<>
      <hr />
      <h4>{i18next.t('attachments')}</h4>
      <Row className="mt-3">
        <Col lg={4}>
          <Stack gap={1}>
            <div className="fw-semibold">{i18next.t('external_pdf')}</div>
            <div className="d-flex align-items-center">
              <span className="">{quoteRequest?.externalPdf?.fileName}</span>
              <Button
                onClick={(e) => downloadExternalPdf(quoteRequest)}
                variant="link"
                className='ms-2'
                size="sm" >
                <FaDownload size={16} title={i18next.t('download_quote_external_pdf')} />
              </Button>
            </div>
          </Stack>
        </Col>
        <Col lg={4}>
          <Stack gap={1}>
            <div className="fw-semibold">{i18next.t('hmp_pdf')}</div>
            <div className="d-flex align-items-center">
              <span className="">{quoteRequest?.hmpPdf?.fileName}</span>
              <Button
                onClick={(e) => downloadHmpPdf(quoteRequest)}
                variant="link"
                className='ms-2'
                size="sm" >
                <FaDownload size={16} title={i18next.t('download_hmp_pdf')} />
              </Button>
            </div>
          </Stack>
        </Col>
      </Row>
    </>);
  }, [quoteRequest])

  if (!quoteRequest?.quote) return null;

  return (
    <Offcanvas className="w-75" show={open} onHide={() => onOpenChange?.(false)} placement={placement}>
      <Offcanvas.Header closeButton style={{ borderBottom: '0.5px solid #ddd' }}>
        <Offcanvas.Title>{i18next.t('stellantis_quote_detail_title')}</Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        <Row>
          {attributes.map((attr, idx) => (<Col key={idx} sm={4} className="my-2">
            <Stack gap={1}>
              <div className="fw-medium">{attr.label}</div>
              <div className="fw-semibold">{attr.value}</div>
            </Stack>
          </Col>))}
        </Row>
        {hasOptionals && renderOptionals()}
        {renderPdfs()}
      </Offcanvas.Body>
    </Offcanvas >
  );
}
